import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from "prop-types";
import { Collapse, Container } from 'reactstrap';
import withRouter from '../../Components/Common/withRouter';

import AdatisLogo from "../../Components/Common/AdatisLogo";
//i18n
import { withTranslation } from "react-i18next";

// Import Data
import { useLayoutMenuData } from "../MenuDataContext";
import VerticalLayout from "../VerticalLayouts";

//SimpleBar
import SimpleBar from "simplebar-react";

const MOBILE_BREAKPOINT = 768;

const TwoColumnLayout = (props : any) => {
    const navData = useLayoutMenuData()().props.children as any[];
    const path = props.router.location.pathname;

    const [isMenu, setIsMenu] = useState(() =>
        typeof document !== "undefined" &&
        document.documentElement.clientWidth < MOBILE_BREAKPOINT
            ? "vertical"
            : "twocolumn"
    );

    const activateIconSidebarActive = useCallback((id: any) => {
        const menu = document.querySelector(
            "#two-column-menu .simplebar-content-wrapper a[sub-items='" + id + "'].nav-icon"
        );
        menu?.classList.add("active");
    }, []);

    const removeActivation = useCallback((items: any) => {
        const activeItems = items.filter((x: any) => x.classList.contains("active"));
        activeItems.forEach((item: any) => {
            if (item.classList.contains("menu-link")) {
                if (!item.classList.contains("active")) {
                    item.setAttribute("aria-expanded", "false");
                }
                item.nextElementSibling?.classList.remove("show");
            }
            if (item.classList.contains("nav-link")) {
                item.nextElementSibling?.classList.remove("show");
                item.setAttribute("aria-expanded", "false");
            }
            item.classList.remove("active");
        });

        const iconMenu = document.getElementById("two-column-menu");
        if (!iconMenu) {
            return;
        }

        const iconItems = iconMenu.getElementsByTagName("a");
        const itemsArray = Array.from(iconItems);
        const activeIconItems = itemsArray.filter((x) => x.classList.contains("active"));
        activeIconItems.forEach((item) => {
            item.classList.remove("active");
            const id = item.getAttribute("sub-items");
            if (!id) {
                return;
            }
            document.getElementById(id)?.classList.remove("show");
        });
    }, []);

    const activateParentDropdown = useCallback((item: any) => {
        item.classList.add("active");
        const parentCollapseDiv = item.closest(".collapse.menu-dropdown");
        if (!parentCollapseDiv?.parentElement?.children?.[0]) {
            return false;
        }

        parentCollapseDiv.classList.add("show");
        const parentTrigger = parentCollapseDiv.parentElement.children[0] as HTMLElement;
        parentTrigger.classList.add("active");
        parentTrigger.setAttribute("aria-expanded", "true");

        if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
            parentCollapseDiv.parentElement.closest(".collapse")?.classList.add("show");
            const parentParentCollapse = parentCollapseDiv.parentElement
                .closest(".collapse")
                ?.previousElementSibling as HTMLElement | null;
            if (parentParentCollapse) {
                parentParentCollapse.classList.add("active");
                parentParentCollapse.closest(".collapse.menu-dropdown")?.classList.add("show");
            }
        }

        activateIconSidebarActive(parentCollapseDiv.getAttribute("id"));
        return false;
    }, [activateIconSidebarActive]);

    const applyResponsiveLayout = useCallback((windowSize: number) => {
        if (windowSize < MOBILE_BREAKPOINT) {
            document.documentElement.setAttribute("data-layout", "vertical");
            setIsMenu("vertical");
            document.body.classList.remove("twocolumn-panel");
            return;
        }

        document.documentElement.setAttribute("data-layout", "twocolumn");
        setIsMenu("twocolumn");
    }, []);

    const initMenu = useCallback(() => {
        const pathName = process.env.PUBLIC_URL + path;
        const ul = document.getElementById("navbar-nav");
        if (!ul) {
            return;
        }

        const items = ul.getElementsByTagName("a");
        const itemsArray = Array.from(items);
        removeActivation(itemsArray);

        const matchingMenuItem = itemsArray.find((x) => x.pathname === pathName);
        if (matchingMenuItem) {
            document.body.classList.remove("twocolumn-panel");
            activateParentDropdown(matchingMenuItem);
            return;
        }

        let id: string;
        if (process.env.PUBLIC_URL) {
            id = pathName.replace(process.env.PUBLIC_URL, "").replace("/", "");
        } else {
            id = pathName.replace("/", "");
        }

        // Keep 2nd column open when React menu state already resolved the module.
        const openPanel = document.querySelector(
            "#navbar-nav .nav-item.twocolumn-item-show"
        );
        if (!openPanel && id && document.documentElement.clientWidth >= MOBILE_BREAKPOINT) {
            document.body.classList.add("twocolumn-panel");
        }
        activateIconSidebarActive(id);
    }, [path, activateParentDropdown, removeActivation, activateIconSidebarActive]);

    const windowResizeHover = useCallback(() => {
        applyResponsiveLayout(document.documentElement.clientWidth);
        initMenu();
    }, [applyResponsiveLayout, initMenu]);

    useLayoutEffect(() => {
        if (props.layoutType !== "twocolumn") {
            return;
        }
        applyResponsiveLayout(document.documentElement.clientWidth);
    }, [props.layoutType, applyResponsiveLayout]);

    useEffect(() => {
        if (props.layoutType !== "twocolumn") {
            return;
        }

        window.addEventListener("resize", windowResizeHover);
        return () => window.removeEventListener("resize", windowResizeHover);
    }, [props.layoutType, windowResizeHover]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        initMenu();
    }, [path, initMenu]);

    /** Native tooltip + a11y for icon-only Two Column rail (labels already i18n via menu.*). */
    const labelText = (entry: any) =>
        props.t(entry.label, entry.labelParams || undefined);

    return (
        <React.Fragment>
            {isMenu === "twocolumn" ?
                <div id="scrollbar">
                    <Container fluid>
                        <div id="two-column-menu">
                            <SimpleBar className="twocolumn-iconview">
                                <Link to="#" className="logo">
                                    <AdatisLogo variant="sm" linkTo={false} />
                                </Link>
                                {(navData || []).map((item : any, key : number) => (
                                    <React.Fragment key={key}>
                                        {item.icon && (
                                            <li>
                                                <Link
                                                    onClick={item.click}
                                                    to={item.link ? item.link : "/#"}
                                                    sub-items={item.id}
                                                    className={`nav-icon${item.stateVariables ? " active" : ""}`}
                                                    data-bs-toggle="collapse"
                                                    title={labelText(item)}
                                                    aria-label={labelText(item)}
                                                    aria-current={item.stateVariables ? "page" : undefined}>
                                                    <i className={item.icon} aria-hidden="true"></i>
                                                </Link>
                                            </li>
                                        )}
                                    </React.Fragment>
                                ))}

                            </SimpleBar>
                        </div>
                        <SimpleBar id="navbar-nav" className="navbar-nav">
                            {(navData || []).map((item : any, key : number) => (
                                <React.Fragment key={key}>
                                    {item.subItems ? (
                                        <li className={`nav-item${item.stateVariables ? " twocolumn-item-show" : ""}`}>
                                            {item.stateVariables ? (
                                                <div className="twocolumn-module-header" role="heading" aria-level={2}>
                                                    {item.icon ? (
                                                        <i className={`${item.icon} twocolumn-module-header__icon`} aria-hidden="true"></i>
                                                    ) : null}
                                                    <span className="twocolumn-module-header__title">
                                                        {labelText(item)}
                                                    </span>
                                                </div>
                                            ) : null}
                                            <Collapse
                                                className="menu-dropdown"
                                                isOpen={item.stateVariables}
                                                id={item.id}>
                                                <ul className="nav nav-sm flex-column">
                                                    {/* Leaf modules: keep one hidden link for path match; header already shows the name. */}
                                                    {item.subItems?.length === 1 &&
                                                    item.subItems[0].link === item.link &&
                                                    item.subItems[0].label === item.label ? (
                                                        <li className="nav-item d-none">
                                                            <Link
                                                                to={item.subItems[0].link}
                                                                className="nav-link"
                                                            >
                                                                {labelText(item.subItems[0])}
                                                            </Link>
                                                        </li>
                                                    ) : null}
                                                    {/* subItems  */}
                                                    {item.subItems &&
                                                    !(
                                                        item.subItems.length === 1 &&
                                                        item.subItems[0].link === item.link &&
                                                        item.subItems[0].label === item.label
                                                    ) &&
                                                    ((item.subItems || []).map((subItem : any, key : number) => (
                                                        <React.Fragment key={key}>
                                                            {!subItem.isChildItem ? (
                                                                <li className="nav-item">
                                                                    <Link
                                                                        to={subItem.link ? subItem.link : "/#"}
                                                                        className="nav-link"
                                                                        title={labelText(subItem)}
                                                                        aria-label={labelText(subItem)}
                                                                    >
                                                                        {labelText(subItem)}
                                                                        {subItem.badgeName ?
                                                                            <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">{subItem.badgeName}</span>
                                                                            : null}
                                                                    </Link>
                                                                </li>
                                                            ) : (
                                                                <li className="nav-item">
                                                                    <Link
                                                                        onClick={subItem.click}
                                                                        className="nav-link"
                                                                        to="/#"
                                                                        data-bs-toggle="collapse"
                                                                        aria-expanded={subItem.stateVariables}
                                                                        title={labelText(subItem)}
                                                                        aria-label={labelText(subItem)}
                                                                    > {labelText(subItem)}
                                                                        {subItem.badgeName ?
                                                                            <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">{subItem.badgeName}</span>
                                                                            : null}
                                                                    </Link>
                                                                    <Collapse className="menu-dropdown" isOpen={subItem.stateVariables} id={subItem.id}>
                                                                        <ul className="nav nav-sm flex-column">
                                                                            {/* child subItems  */}
                                                                            {subItem.childItems && (
                                                                                (subItem.childItems || []).map((childItem : any, key : number) => (
                                                                                    <React.Fragment key={key}>
                                                                                        {!childItem.isChildItem ? (
                                                                                            <li className="nav-item">
                                                                                                <Link
                                                                                                    to={childItem.link ? childItem.link : "/#"}
                                                                                                    className="nav-link"
                                                                                                    title={labelText(childItem)}
                                                                                                    aria-label={labelText(childItem)}
                                                                                                >
                                                                                                    {labelText(childItem)}
                                                                                                </Link>
                                                                                            </li>
                                                                                        ) : (
                                                                                            <li className="nav-item" key={key}>
                                                                                                <Link
                                                                                                    to={childItem.link ? childItem.link : "/#"}
                                                                                                    onClick={childItem.click}
                                                                                                    data-bs-toggle="collapse"
                                                                                                    aria-expanded={childItem.stateVariables}
                                                                                                    className="nav-link"
                                                                                                    title={labelText(childItem)}
                                                                                                    aria-label={labelText(childItem)}>
                                                                                                    {labelText(childItem)}
                                                                                                </Link>
                                                                                                <Collapse className="menu-dropdown" isOpen={childItem.stateVariables} id={childItem.id}>
                                                                                                    <ul className="nav nav-sm flex-column">
                                                                                                        {/* child subChildItems  */}
                                                                                                        {childItem.isChildItem && (
                                                                                                            (childItem.childItems || []).map((childItem: any, key: number) => (
                                                                                                                <li className="nav-item" key={key} >
                                                                                                                    <Link
                                                                                                                        to={childItem.link ? childItem.link : "/#"}
                                                                                                                        className="nav-link"
                                                                                                                        title={labelText(childItem)}
                                                                                                                        aria-label={labelText(childItem)}>
                                                                                                                        {labelText(childItem)}
                                                                                                                    </Link>
                                                                                                                </li>
                                                                                                            ))
                                                                                                        )}
                                                                                                    </ul>
                                                                                                </Collapse>
                                                                                            </li>
                                                                                        )}
                                                                                    </React.Fragment>
                                                                                ))
                                                                            )}
                                                                        </ul>
                                                                    </Collapse>
                                                                </li>
                                                            )}
                                                        </React.Fragment>
                                                    ))
                                                    )}
                                                </ul>

                                            </Collapse>
                                        </li>
                                    ) : null
                                    }
                                </React.Fragment>
                            ))}
                        </SimpleBar>
                    </Container>
                </div>
                :
                <SimpleBar id="scrollbar" className="h-100">
                    <Container fluid>
                        <div id="two-column-menu"></div>
                        <ul className="navbar-nav" id="navbar-nav">
                            <VerticalLayout />
                        </ul>
                    </Container>
                </SimpleBar>
            }
        </React.Fragment >
    );
};

TwoColumnLayout.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
};

export default withRouter(withTranslation()(TwoColumnLayout));